import React, { useEffect, useMemo, useState } from "react";
import { api, formatEURDecimal, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, X, Trash2, Pencil, Search, Package, ArrowUp, ArrowDown, Store, EyeOff } from "lucide-react";

const PRICING_MODES = {
  flat: { label: "Forfaitaire (une fois)", short: "forfait" },
  per_day: { label: "Journalier", short: "/ jour" },
};

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none";
const labelCls = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";

const EMPTY = {
  name: "", description: "", category: "", price: "", price_cap: "", pricing_mode: "flat",
  shop_visible: true, sku: "", stock_quantity: "", internal_cost: "", stock_by_location: {},
  max_per_booking: "", min_days: "", max_days: "", required: false,
  applies_to_categories: [], required_categories: [],
};

export default function ProductsSection() {
  const [products, setProducts] = useState(null);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);

  const load = () => api.get("/vendor/products").then((r) => setProducts(r.data.products || [])).catch(() => setProducts([]));
  const loadStats = () => api.get("/vendor/products/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  useEffect(() => {
    load();
    loadStats();
    api.get("/vendor/locations").then((r) => setLocations(r.data.locations || [])).catch(() => {});
    api.get("/vendor/settings").then((r) => setCategories(r.data.settings?.fleet?.categories || r.data.fleet?.categories || [])).catch(() => {});
  }, []);
  const statsByProduct = useMemo(() => Object.fromEntries(((stats?.stats) || []).map((s) => [s.product_id, s])), [stats]);
  const catLabel = (val) => categories.find((c) => c.value === val)?.label || val;

  const filtered = useMemo(() => {
    if (!products) return [];
    const n = search.trim().toLowerCase();
    if (!n) return products;
    return products.filter((p) => [p.name, p.category, p.sku, p.reference].some((f) => (f || "").toLowerCase().includes(n)));
  }, [products, search]);

  const startCreate = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const startEdit = (p) => {
    setForm({
      name: p.name, description: p.description || "", category: p.category || "", price: p.price ?? "",
      price_cap: p.price_cap ?? "", pricing_mode: p.pricing_mode === "per_day" ? "per_day" : "flat",
      shop_visible: p.shop_visible !== false, sku: p.sku || "",
      stock_quantity: p.stock_quantity ?? "", internal_cost: p.internal_cost ?? "",
      stock_by_location: p.stock_by_location || {},
      max_per_booking: p.max_per_booking ?? "", min_days: p.min_days ?? "", max_days: p.max_days ?? "",
      required: !!p.required, applies_to_categories: p.applies_to_categories || [], required_categories: p.required_categories || [],
    });
    setEditingId(p.id);
    setShowForm(true);
  };

  const toggleCat = (field, val) => {
    setForm((f) => {
      const cur = f[field] || [];
      const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
      // required_categories doit rester un sous-ensemble des catégories autorisées
      if (field === "applies_to_categories") {
        const rq = (f.required_categories || []).filter((x) => next.includes(x));
        return { ...f, applies_to_categories: next, required_categories: rq };
      }
      return { ...f, [field]: next };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.min_days !== "" && form.max_days !== "" && parseInt(form.min_days) > parseInt(form.max_days)) {
      toast.error("La durée minimale doit être inférieure ou égale à la durée maximale.");
      return;
    }
    setSaving(true);
    const stockByLoc = {};
    Object.entries(form.stock_by_location || {}).forEach(([k, v]) => { if (v !== "" && v != null) stockByLoc[k] = parseInt(v); });
    const payload = {
      ...form,
      price: form.price === "" ? 0 : parseFloat(form.price),
      price_cap: form.pricing_mode === "per_day" && form.price_cap !== "" ? parseFloat(form.price_cap) : null,
      stock_quantity: form.stock_quantity === "" ? null : parseInt(form.stock_quantity),
      internal_cost: form.internal_cost === "" ? 0 : parseFloat(form.internal_cost),
      max_per_booking: form.max_per_booking === "" ? null : parseInt(form.max_per_booking),
      min_days: form.min_days === "" ? null : parseInt(form.min_days),
      max_days: form.max_days === "" ? null : parseInt(form.max_days),
      stock_by_location: stockByLoc,
    };
    try {
      if (editingId) {
        await api.patch(`/vendor/products/${editingId}`, payload);
        toast.success("Option mise à jour");
      } else {
        const { data } = await api.post("/vendor/products", payload);
        toast.success(`Option ${data.product.reference} créée`);
      }
      setShowForm(false);
      setEditingId(null);
      load();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  const toggle = async (p, field) => {
    try { await api.patch(`/vendor/products/${p.id}`, { [field]: !(p[field] !== false) }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer l'option "${p.name}" ? Les réservations et factures déjà émises conservent leur snapshot.`)) return;
    try { await api.delete(`/vendor/products/${p.id}`); toast.success("Option supprimée"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
  };

  const move = async (p, dir) => {
    const ids = products.map((x) => x.id);
    const i = ids.indexOf(p.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setProducts(ids.map((id) => products.find((x) => x.id === id)));
    await api.post("/vendor/products/reorder", { ids }).catch(() => load());
  };

  if (products === null) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>;

  return (
    <div className="space-y-4" data-testid="products-section">
      {stats && stats.totals.sales > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="products-totals">
          <TotalTile label="Ventes" value={stats.totals.sales} />
          <TotalTile label="CA généré" value={formatEURDecimal(stats.totals.revenue)} />
          <TotalTile label="Coûts internes" value={formatEURDecimal(stats.totals.cost)} />
          <TotalTile label="Marge options" value={formatEURDecimal(stats.totals.margin)} accent />
        </div>
      )}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une option, une catégorie, une référence…" className={inputCls + " pl-9"} data-testid="products-search" />
          </div>
          <button onClick={() => (showForm ? setShowForm(false) : startCreate())} className="inline-flex items-center justify-center gap-1.5 h-[38px] px-4 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 shrink-0" data-testid="add-product-btn">
            {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showForm ? "Fermer" : "Nouvelle option"}
          </button>
        </div>

        {showForm && (
          <form onSubmit={submit} className="rounded-2xl border border-violet-200 bg-violet-50/30 p-4 grid sm:grid-cols-3 gap-3" data-testid="product-form">
            <div>
              <label className={labelCls}>Nom *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Siège bébé" className={inputCls + " mt-1"} data-testid="product-name" />
            </div>
            <div>
              <label className={labelCls}>Famille (libre)</label>
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Confort, Assurance…" className={inputCls + " mt-1"} data-testid="product-category" />
            </div>
            <div>
              <label className={labelCls}>Référence interne (SKU)</label>
              <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="Optionnel" className={inputCls + " mt-1"} data-testid="product-sku" />
            </div>
            <div className="sm:col-span-3">
              <label className={labelCls}>Description</label>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Visible en boutique sous le nom de l'option" className={inputCls + " mt-1"} data-testid="product-description" />
            </div>

            {/* Mode tarifaire — deux modes exclusifs */}
            <div className="sm:col-span-3">
              <label className={labelCls}>Mode tarifaire</label>
              <div className="mt-1 inline-flex rounded-xl border border-slate-200 overflow-hidden" data-testid="product-pricing-mode">
                {Object.entries(PRICING_MODES).map(([v, m]) => (
                  <button type="button" key={v} onClick={() => setForm({ ...form, pricing_mode: v })}
                    className={`px-4 py-2 text-xs font-semibold ${form.pricing_mode === v ? "bg-violet-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`product-mode-${v}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Prix TTC (€) {form.pricing_mode === "per_day" ? "/ jour" : "/ réservation"}</label>
              <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls + " mt-1"} data-testid="product-price" />
            </div>
            {form.pricing_mode === "per_day" && (
              <div>
                <label className={labelCls}>Plafond de facturation (€ / unité)</label>
                <input type="number" min="0" step="0.01" value={form.price_cap} onChange={(e) => setForm({ ...form, price_cap: e.target.value })} placeholder="Ex. 45 €" className={inputCls + " mt-1"} data-testid="product-price-cap" />
                <p className="text-[10px] text-slate-400 mt-1">Montant maximum facturé par unité sur une location.</p>
              </div>
            )}
            <div>
              <label className={labelCls}>Quantité max / réservation</label>
              <input type="number" min="1" value={form.max_per_booking} onChange={(e) => setForm({ ...form, max_per_booking: e.target.value })} placeholder="Illimité" className={inputCls + " mt-1"} data-testid="product-max-per-booking" />
            </div>

            {/* Conditions de durée */}
            <div>
              <label className={labelCls}>Durée min. (jours)</label>
              <input type="number" min="1" value={form.min_days} onChange={(e) => setForm({ ...form, min_days: e.target.value })} placeholder="Aucune" className={inputCls + " mt-1"} data-testid="product-min-days" />
            </div>
            <div>
              <label className={labelCls}>Durée max. (jours)</label>
              <input type="number" min="1" value={form.max_days} onChange={(e) => setForm({ ...form, max_days: e.target.value })} placeholder="Aucune" className={inputCls + " mt-1"} data-testid="product-max-days" />
            </div>
            <div className="flex items-end pb-1">
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer" data-testid="product-shop-visible-label">
                <input type="checkbox" checked={form.shop_visible} onChange={(e) => setForm({ ...form, shop_visible: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-600" data-testid="product-shop-visible" />
                Visible en boutique publique
              </label>
            </div>

            {/* Catégories compatibles */}
            {categories.length > 0 && (
              <div className="sm:col-span-3 rounded-xl border border-slate-200 bg-white p-3" data-testid="product-categories">
                <div className={labelCls + " mb-2"}>Catégories compatibles (vide = toutes)</div>
                <div className="flex flex-wrap gap-2">
                  {categories.map((c) => (
                    <button type="button" key={c.value} onClick={() => toggleCat("applies_to_categories", c.value)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${(form.applies_to_categories || []).includes(c.value) ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}
                      data-testid={`product-cat-${c.value}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Obligatoire */}
            <div className="sm:col-span-3 rounded-xl border border-slate-200 bg-white p-3" data-testid="product-required-block">
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-600" data-testid="product-required" />
                Option obligatoire (ajoutée automatiquement, non retirable par le client)
              </label>
              {form.required && (form.applies_to_categories || []).length > 0 && (
                <div className="mt-3">
                  <div className={labelCls + " mb-2"}>Obligatoire uniquement pour ces catégories (vide = toutes les catégories compatibles)</div>
                  <div className="flex flex-wrap gap-2">
                    {(form.applies_to_categories || []).map((val) => (
                      <button type="button" key={val} onClick={() => toggleCat("required_categories", val)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${(form.required_categories || []).includes(val) ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:border-amber-300"}`}
                        data-testid={`product-reqcat-${val}`}>
                        {catLabel(val)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Stock */}
            <div>
              <label className={labelCls}>Quantité en stock</label>
              <input type="number" min="0" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} placeholder="Vide = sans limitation" className={inputCls + " mt-1"} data-testid="product-stock" />
            </div>
            <div>
              <label className={labelCls}>Coût interne (€ / vente)</label>
              <input type="number" min="0" step="0.01" value={form.internal_cost} onChange={(e) => setForm({ ...form, internal_cost: e.target.value })} placeholder="Jamais visible client" className={inputCls + " mt-1"} data-testid="product-internal-cost" />
            </div>
            {locations.length > 0 && form.stock_quantity !== "" && (
              <div className="sm:col-span-3 rounded-xl border border-slate-200 bg-white p-3" data-testid="product-stock-by-location">
                <div className={labelCls + " mb-2"}>Stock par agence (optionnel — prioritaire sur le stock global pour l'agence de départ ; sinon le stock global est mutualisé entre agences)</div>
                <div className="grid sm:grid-cols-3 gap-2">
                  {locations.map((l) => (
                    <div key={l.id} className="flex items-center gap-2">
                      <span className="text-xs text-slate-600 flex-1 truncate">{l.name}</span>
                      <input type="number" min="0" value={form.stock_by_location?.[l.id] ?? ""} onChange={(e) => setForm({ ...form, stock_by_location: { ...form.stock_by_location, [l.id]: e.target.value } })} placeholder="—" className="w-20 h-8 rounded-lg border border-slate-200 px-2 text-xs" data-testid={`product-stock-loc-${l.id}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="sm:col-span-3 flex justify-end">
              <button type="submit" disabled={saving} className="h-[38px] px-6 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="product-submit">
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {editingId ? "Enregistrer" : "Créer l'option"}
              </button>
            </div>
          </form>
        )}

        {filtered.length === 0 ? (
          <div className="py-12 text-center rounded-2xl border border-dashed border-slate-200" data-testid="products-empty">
            <Package className="h-7 w-7 mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-500">
              {products.length === 0 ? "Aucune option. Créez votre première option (siège bébé, GPS, nettoyage…)." : "Aucune option pour cette recherche."}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((p, idx) => {
              const mode = PRICING_MODES[p.pricing_mode] || PRICING_MODES.flat;
              const inactive = p.active === false;
              return (
                <div key={p.id} className={`flex items-start gap-3 rounded-xl border bg-white p-3.5 ${inactive ? "border-slate-100 opacity-60" : "border-slate-200"}`} data-testid={`product-row-${p.id}`}>
                  {!search && (
                    <div className="flex flex-col shrink-0">
                      <button onClick={() => move(p, -1)} disabled={idx === 0} className="h-5 w-5 rounded hover:bg-slate-100 text-slate-400 disabled:opacity-30 inline-flex items-center justify-center" data-testid={`product-up-${p.id}`}><ArrowUp className="h-3 w-3" /></button>
                      <button onClick={() => move(p, 1)} disabled={idx === filtered.length - 1} className="h-5 w-5 rounded hover:bg-slate-100 text-slate-400 disabled:opacity-30 inline-flex items-center justify-center" data-testid={`product-down-${p.id}`}><ArrowDown className="h-3 w-3" /></button>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-bold text-slate-400">{p.reference}</span>
                      <span className="text-sm font-semibold text-slate-900">{p.name}</span>
                      {p.required && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold" data-testid={`product-required-badge-${p.id}`}>Obligatoire</span>}
                      {p.category && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold">{p.category}</span>}
                      {p.sku && <span className="text-[10px] text-slate-400 font-mono">{p.sku}</span>}
                      {inactive && <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-500 text-[10px] font-bold">Désactivé</span>}
                    </div>
                    {p.description && <div className="text-xs text-slate-500 mt-0.5">{p.description}</div>}
                    <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-2">
                      {p.shop_visible !== false ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"><Store className="h-3 w-3" /> Boutique</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-slate-400"><EyeOff className="h-3 w-3" /> Interne uniquement</span>
                      )}
                      {(p.applies_to_categories || []).length > 0 && (
                        <span className="text-[10px] text-slate-400">Catégories : {(p.applies_to_categories).map(catLabel).join(", ")}</span>
                      )}
                      {(p.min_days != null || p.max_days != null) && (
                        <span className="px-1.5 py-0.5 rounded-full bg-slate-50 text-slate-600 text-[10px] font-bold" data-testid={`product-duration-badge-${p.id}`}>
                          {p.min_days != null ? `≥ ${p.min_days}j` : ""}{p.min_days != null && p.max_days != null ? " · " : ""}{p.max_days != null ? `≤ ${p.max_days}j` : ""}
                        </span>
                      )}
                      {p.max_per_booking != null && <span className="px-1.5 py-0.5 rounded-full bg-slate-50 text-slate-600 text-[10px] font-bold">max {p.max_per_booking}/résa</span>}
                      {p.stock_quantity != null && (
                        <span className="px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 text-[10px] font-bold" data-testid={`product-stock-badge-${p.id}`}>
                          Stock : {p.stock_quantity}{p.stock_by_location && Object.keys(p.stock_by_location).length > 0 ? " (+ par agence)" : ""}
                        </span>
                      )}
                      {statsByProduct[p.id] && (
                        <span className="px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 text-[10px] font-bold" data-testid={`product-stats-badge-${p.id}`}>
                          {statsByProduct[p.id].sales} vente{statsByProduct[p.id].sales > 1 ? "s" : ""} · CA {formatEURDecimal(statsByProduct[p.id].revenue)}
                          {(p.internal_cost || 0) > 0 && <> · marge {formatEURDecimal(statsByProduct[p.id].margin)}</>}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-slate-900" data-testid={`product-price-display-${p.id}`}>
                      {formatEURDecimal(p.price)} <span className="text-[10px] text-slate-400 font-normal">{mode.short}</span>
                    </div>
                    {p.pricing_mode === "per_day" && p.price_cap != null && (
                      <div className="text-[10px] text-slate-400 font-normal" data-testid={`product-price-cap-display-${p.id}`}>plafond {formatEURDecimal(p.price_cap)}</div>
                    )}
                    <div className="flex items-center justify-end gap-1 mt-1.5">
                      <button onClick={() => toggle(p, "active")} className={`h-7 px-2 rounded-md text-[10px] font-semibold ${inactive ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-500 hover:bg-slate-100"}`} data-testid={`product-toggle-active-${p.id}`}>
                        {inactive ? "Activer" : "Désactiver"}
                      </button>
                      <button onClick={() => startEdit(p)} className="h-7 w-7 rounded-md hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center" title="Modifier" data-testid={`product-edit-${p.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => remove(p)} className="h-7 w-7 rounded-md hover:bg-red-50 text-red-500 inline-flex items-center justify-center" title="Supprimer" data-testid={`product-delete-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <p className="text-xs text-slate-400">
        Les options actives sont automatiquement proposées dans les réservations manuelles, les devis et (si « Visible en boutique ») au checkout public. Les prix sont TTC et calculés côté serveur. La disponibilité est calculée en temps réel depuis les réservations. Les options système (rachat de franchise, retour dans une autre agence, surcoûts tarifaires) restent pilotées dans Légal et par agence.
      </p>
    </div>
  );
}

function TotalTile({ label, value, accent }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${accent ? "bg-violet-50 border-violet-200" : "bg-white border-slate-200"}`}>
      <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">{label}</div>
      <div className={`font-heading text-lg font-medium mt-0.5 ${accent ? "text-violet-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
